import { toObj } from 'common/utils/arrayUtils'
import types from '../types'
import { DEFAULT_MODE_IDS, NO_RESULTS_MODE } from 'tickets/utils/constants'

const createNewModeInfo = (modeId) => ({
  name: modeId,
  hourOfDayAware: false,
  isError: false,
  hasLoadedOutbound: false,
  hasLoadedInbound: false,
  isPolling: false,
  canFetchMoreOutbound: false,
  canFetchMoreInbound: false,
  noResultsForTimeOutbound: false,
  noResultsForTimeInbound: false,
  noResultsForFerryType: false,
  supportsFerryFootPassenger: false,
  supportsFerryCarPassenger: false,
  limitedResultsLoaded: false,
  messages: [],
  errors: [],
  page: 0,
})

const initialState = toObj(DEFAULT_MODE_IDS, modeId => modeId, createNewModeInfo)

const modesInfoReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.RESPONSE_SEARCH_FAILED:
      return handleSearchFailed(state, action.modeId)
    case types.MODE_LOADING:
      return setModeLoading(state, action.payload, true, true)
    case types.MODE_LOADED:
      return setModeLoaded(state, action.payload)
    case types.MODE_POLLING:
      return setModePolling(state, action.modeId)
    case types.API_MESSAGES_SET:
      return setModeMessages(state, action.modeId, action.messages, action.errors)
    case types.PAGE_INCREASE:
      return handlePageIncrease(state, action.payload, action.isOutbound)
    case types.RESPONSE_REPLACE:
      return replaceResponse(state, action.modeId)
    case types.NO_RESULTS_FOR_TIME:
      return handleNoMoreResultsForTime(state, action.modeId, action.isOutbound)
    case types.NO_RESULTS_FOR_FERRY_TYPE:
      return handleNoResultsForFerryType(state, action.modeId)
    case types.NO_MORE_RESULTS:
      return handleNoMoreResults(state, action.modeId, action.isOutbound)
    case types.TIME_CHANGE:
      return setModeLoading(state, action.modeId, action.isOutbound, !action.isOutbound)
    case types.UPDATE_TRANSFER:
      return setModeLoading(state, action.modeId, true, true)
    case types.FERRY_TYPE_CHANGE:
      return setModeLoading(state, action.modeId, true, true)
    case types.CONFIG_RECEIVED:
      return handleConfigReceived(state, action.payload)
    case types.RESPONSE_ADD:
      return addResponse(state, action.mode)
    case types.RESPONSES_LOADING:
      return setAllModesLoading(state)
    default:
      return state
  }
}

const handleSearchFailed = (state, modeId) => {
  const newModeInfo = {
    ...state[modeId],
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
    isError: true,
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

// Returns an updated state with a specified tab's loading state set
const setModeLoading = (state, modeId, setOutbound, setInbound) => {
  const hasLoadedOutbound = (setOutbound || !state[modeId])
    ? false
    : state[modeId].hasLoadedOutbound
  const hasLoadedInbound = (setInbound || !state[modeId])
    ? false
    : state[modeId].hasLoadedInbound

  const newMode = {
    ...state[modeId],
    isError: false,
    hasLoadedOutbound,
    hasLoadedInbound,
    canFetchMoreOutbound: calculateCanFetchMore(modeId, state[modeId].hourOfDayAware),
    canFetchMoreInbound: calculateCanFetchMore(modeId, state[modeId].hourOfDayAware),
    noResultsForTimeInbound: false,
    noResultsForTimeOutbound: false,
    limitedResultsLoaded: false,
  }

  return {
    ...state,
    [modeId]: newMode,
  }
}

const setModeLoaded = (state, modeId) => {
  const newMode = {
    ...state[modeId],
    isPolling: false,
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
  }

  return {
    ...state,
    [modeId]: newMode,
  }
}

const setModePolling = (state, modeId) => {
  const newMode = {
    ...state[modeId],
    hasLoadedOutbound: false,
    hasLoadedInbound: false,
    isPolling: true,
  }

  return {
    ...state,
    [modeId]: newMode,
  }
}

const setAllModesLoading = (state) => {
  const newState = { ...state }
  // TODO: This doesn't set non-default modes to loading
  DEFAULT_MODE_IDS.forEach((modeId) => {
    // Reset modeInfo and make it loading
    newState[modeId] = {
      ...createNewModeInfo(modeId),
    }
  })

  return newState
}

// Returns an updated state with a specified tab's messages updated
const setModeMessages = (state, modeId, messages, errors) => {
  const newMode = {
    ...state[modeId],
    messages,
    errors,
  }

  return {
    ...state,
    [modeId]: newMode,
  }
}

// todo: only change page for outbound OR inbound
const handlePageIncrease = (state, modeId) => {
  const newPage = state[modeId].page + 1

  const newModeInfo = {
    ...state[modeId],
    page: newPage,
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

const replaceResponse = (state, modeId) => {
  // Set the mode info
  const newModeInfo = {
    ...state[modeId],
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
    isPolling: false,
    isError: false,
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

const handleNoMoreResultsForTime = (state, modeId, isOutbound) => {
  const newModeInfo = {
    ...state[modeId],
    isError: true,
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
    isPolling: false,
  }

  if (isOutbound) {
    newModeInfo.canFetchMoreOutbound = false
    newModeInfo.noResultsForTimeOutbound = true
  } else {
    newModeInfo.canFetchMoreInbound = false
    newModeInfo.noResultsForTimeInbound = true
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

const handleNoResultsForFerryType = (state, modeId) => {
  const newModeInfo = {
    ...state[modeId],
    isError: true,
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
    isPolling: false,
    noResultsForFerryType: true,
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

const handleNoMoreResults = (state, modeId, isOutbound) => {
  const newModeInfo = { ...state[modeId] }
  if (isOutbound) {
    newModeInfo.canFetchMoreOutbound = false
  } else {
    newModeInfo.canFetchMoreInbound = false
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

// Update configuration status, create modesInfo for each returned kind
const handleConfigReceived = (state, payload) => {
  const kinds = payload.transitKinds

  // Populate default tabs so they still show even if not applicable to search
  const newModesInfo = {}

  DEFAULT_MODE_IDS.forEach((modeId) => {
    newModesInfo[modeId] = {
      ...createNewModeInfo(modeId),
      hasLoadedOutbound: true,
      hasLoadedInbound: true,
    }
  })

  kinds.forEach((kind) => {
    newModesInfo[kind.transitKind] = {
      ...createNewModeInfo(kind.transitKind),
      hourOfDayAware: kind.hourOfDayAware,
      canFetchMoreOutbound: calculateCanFetchMore(kind.transitKind, kind.hourOfDayAware),
      canFetchMoreInbound: calculateCanFetchMore(kind.transitKind, kind.hourOfDayAware),
      supportsFerryFootPassenger: kind.supportsFerryFootPassenger,
      supportsFerryCarPassenger: kind.supportsFerryCarPassenger,
    }
  })

  if (kinds.length == 0) {
    newModesInfo[NO_RESULTS_MODE] = {
      ...createNewModeInfo(NO_RESULTS_MODE),
      hasLoadedOutbound: true,
      hasLoadedInbound: true,
    }
  }

  return newModesInfo
}

// Returns a state with a response merged in
const addResponse = (state, modeId) => {
  const newModeInfo = {
    ...state[modeId],
    hasLoadedOutbound: true,
    hasLoadedInbound: true,
    isError: false,
  }

  return {
    ...state,
    [modeId]: newModeInfo,
  }
}

/** HELPERS **/

const calculateCanFetchMore = (modeId, hourOfDayAware) => {
  // Airport-transfers ARE hourOfDayAware (you can specify a time), but you cannot fetch more
  // (all services depart at the selected time, there's no 'later' service)
  const isAirportTransfer = modeId === 'airport-transfer'

  return isAirportTransfer ? false : hourOfDayAware
}

export default modesInfoReducer
